import { useState, useEffect } from "react";
import axiosInstance from "@/utils/axiosInstance";
import { DateRange } from "react-day-picker";
import { taxiStatusItems } from "@/constants/status";

interface StatusData {
  status: string;
  total: number;
}

interface StatusMenuItem {
  item: string;
  label: string;
  status: string;
  total: number;
}

export const useStatusManagement = (dateRange: DateRange) => {
  const [statusMenu, setStatusMenu] = useState<StatusMenuItem[]>([]);
  const [statusSummary, setStatusSummary] = useState<Map<string, number>>(new Map());
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [filteredOrdersCount, setFilteredOrdersCount] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("NEW_ORDER");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  const updateStatusMenu = async () => {
    try {
      const { data }: { data: StatusData[] } = await axiosInstance.get(
        `taxi-orders/total-by-status?pickupTime=${dateRange.from?.toISOString()}&endPickupTime=${dateRange.to?.toISOString()}`
      );

      const _statusSummary = new Map(data.map(item => [item.status, item.total]));
      setStatusSummary(_statusSummary);

      const _totalOrders = data.reduce((acc: number, item: StatusData) => acc + item.total, 0);
      setTotalOrdersCount(_totalOrders);

      // Calculate filtered count
      if (selectedStatus === "ALL") {
        if (statusFilter.length > 0) {
          const filteredCount = data
            .filter(item => statusFilter.includes(item.status))
            .reduce((acc: number, item: StatusData) => acc + item.total, 0);
          setFilteredOrdersCount(filteredCount);
        } else {
          setFilteredOrdersCount(_totalOrders);
        }
      } else {
        setFilteredOrdersCount(data.find(item => item.status === selectedStatus)?.total || 0);
      }

      // Create status menu
      const statusMenuTemp = taxiStatusItems
        .filter(item => item.display)
        .map(item => {
          const found = data.find(d => d.status === item.status);
          return {
            total: found ? found.total : 0,
            label: item.label,
            status: item.status,
            item: item.status,
          };
        });
      setStatusMenu(statusMenuTemp);
    } catch (error) {
      console.error("Error updating status menu:", error);
    }
  };

  const handleFilterChange = (status: string) => {
    setSelectedStatus(status);
    if (status === "ALL") {
      setFilteredOrdersCount(totalOrdersCount);
    } else {
      setFilteredOrdersCount(statusSummary.get(status) || 0);
    }
  };

  const updateStatusFilter = (selected: string[]) => {
    setStatusFilter(selected);
    if (selected.length > 0) {
      const filteredCount = statusMenu
        .filter(item => selected.includes(item.status))
        .reduce((acc, item) => acc + item.total, 0);
      setFilteredOrdersCount(filteredCount);
    } else {
      setFilteredOrdersCount(totalOrdersCount);
    }
  };

  const resetStatusFilter = () => {
    setStatusFilter([]);
    setFilteredOrdersCount(totalOrdersCount);
  };

  useEffect(() => {
    updateStatusMenu();
  }, [dateRange]);

  return {
    statusMenu,
    statusSummary,
    totalOrdersCount,
    filteredOrdersCount,
    selectedStatus,
    statusFilter,
    handleFilterChange,
    updateStatusFilter,
    resetStatusFilter,
    updateStatusMenu,
  };
};